import { createSlice } from "@reduxjs/toolkit";
import {
  removeToken,
  setToken,
  setRememberMe,
  serviceSigninUser,
  removeRememberMe,
  serviceGetAllParameters,
  serviceChangePassword
} from 'utils';
// import jwt from "jwt-simple";


const initialState = {
  user: null,
  roles: [],
  roleSelected: {
    permission: [],
    roleDescription: "",
    roleId: -1,
    roleName: ""
  },
  parameters: null,
  filters: {
    project: null
  },
  token: null,
  isSignedIn: false,
  error: {
    password: null,
    auth: null,
    params: null
  },
  success: {
    password: null,
    auth: null,
    params: null
  },
  isLoaded: {
    password: false,
    auth: false,
    params: false
  }
};

function validJsonFormat(object) {
  try{
    JSON.parse(object);
  }catch (e){
    return JSON.stringify(object);
  }
  return object;
}

function validObjectFormat(object) {
  if (typeof object === 'object') {
    return object
  } else return JSON.parse(object);
}


function formatRolesDto(roles) {
  const newRoles = roles.map(role => {
    const permission = role.permission.reduce((after, current) => {
      return { ...after, [current.projectId]: current.permissions.reduce((after, current) => {
        return { ...after, [current.view]: current };
      }, {}) };
    }, {});

    return {
      ...role, permission
    }
  });

  return newRoles
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {

    signInSuccess(state, action) {
      // let user = jwt.decode(action.payload.token);
      let user = action.payload.token;
      let roles = [];
      let roleSelected = null;

      if (user) {
        setToken(validJsonFormat(action.payload.token), action.payload.keepSignedIn);
        window.dispatchEvent(new Event('storage'));
        setRememberMe(action.payload.keepSignedIn);
        user = validObjectFormat(action.payload.token);

        if (user.roles && user.roles.length > 0) {
          roles = formatRolesDto(user.roles);
          roleSelected = roles[0];
        }

        return {
          ...state,
          token: action.payload.token,
          success: { ...state.success, auth: true },
          isLoaded: {...state.isLoaded, auth: false},
          isSignedIn: true,
          user: { ...user },
          roles: roles,
          roleSelected: roleSelected || state.roleSelected
        }
      } else {
        removeToken();
        removeRememberMe();
        return {
          ...state,
          success: { ...state.success, auth: null},
          isLoaded: {...state.isLoaded, auth: false},
        }
      }
    },

    getParametersSuccess(state, action) {
      return {
        ...state,
        isLoaded: {...state.isLoaded, params: false},
        parameters: action.payload
      };
    },

    signInRequest(state) {
      return {
        ...state,
        isLoaded: {...state.isLoaded, auth: true},
      };
    },

    getParametersRequest(state) {
      return {
        ...state,
        isLoaded: { ...state.isLoaded, params: true}
      }
    },

    signInFailure(state, action) {
      return {
        ...state,
        isLoaded: {...state.isLoaded, auth: false},
        error: {...state.error, auth: action.payload.message}
      }
    },

    getParametersFailure(state, action) {
      return {
        ...state,
        isLoaded: {...state.isLoaded, params: false},
        error: {...state.error, params: action.payload.message}
      }
    },

    changePasswordRequest(state) {
      return {
        ...state,
        isLoaded: { ...state.isLoaded, password: true }
      }
    },

    changePasswordSuccess(state, action) {
      return {
        ...state,
        isLoaded: {...state.isLoaded, password: false},
        success: {...state.success, password: action.payload},
      }
    },

    changePasswordFailure(state, action) {
      return {
        ...state,
        isLoaded: {...state.isLoaded, password: false},
        error: {...state.error, password: action.payload.message}
      }
    },

    signOut(state) {
      removeToken();
      removeRememberMe();
      return initialState;
    },

    setFilters(state, action) {
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload
        }
      };
    },

    setRoleSelected(state, action) {
      const roleFinded = state.roles.find((role) => role.roleId === action.payload);

      if (roleFinded) {
        return { ...state, roleSelected: roleFinded }
      }
      return state;
    },

    cleanUpAuth(state) {
      return initialState
    },

    cleanUpMessageAuth(state) {
      return {
        ...state,
        isLoaded: initialState.isLoaded,
        error: initialState.error,
        success: initialState.success
      }
    },

    cleanUpPassword(state) {
      return {
        ...state,
        isLoaded: {...state.isLoaded, password: false},
        error: {...state.error, password: null},
        success: {...state.success, password: null}
        }
      }
  }
});

const {
  signInRequest,
  signInSuccess,
  signInFailure,
  getParametersRequest,
  getParametersSuccess,
  getParametersFailure,
  changePasswordRequest,
  changePasswordSuccess,
  changePasswordFailure,
  signOut,
  setFilters,
  setRoleSelected,
  cleanUpAuth,
  cleanUpPassword,
  cleanUpMessageAuth
} = authSlice.actions;

const fetchSignIn = (data, keepSignedIn) => {
  return (dispatch) => {
    dispatch(signInRequest());
    serviceSigninUser(data)
    .then(response => {
      if (response.data === -1) dispatch(signInFailure({ message: 'login.alert.error' }));
      else if (response.data === -2) dispatch(signInFailure({ message: 'login.alert.blocked' }));
      else dispatch(signInSuccess({ token: response.data, keepSignedIn }));
    })
    .catch(err => dispatch(signInFailure(err)));
  };
};

const fetchParameters = (data) => {
  return (dispatch) => {
    dispatch(getParametersRequest());
    serviceGetAllParameters(data)
    .then(response => dispatch(getParametersSuccess(response.data)))
    .catch(err => dispatch(getParametersFailure(err)))
  }
}

const fetchParametersNoLoader = (data) => {
  return (dispatch) => {
    serviceGetAllParameters(data)
    .then(response => dispatch(getParametersSuccess(response.data)))
    .catch(err => dispatch(getParametersFailure(err)))
  }
}

const fetchSignOut = (payload) => (dispatch) => dispatch(signOut(payload));
const authUser = (payload) => (dispatch) => dispatch(signInSuccess(payload));

const setFiltersDispatch = (payload) => {
  return (dispatch) => dispatch(setFilters(payload));
}

const setRoleSelectedDispatch = (payload) => {
  return (dispatch) => dispatch(setRoleSelected(payload));
}

const fetchChangePassword = (payload) => {
  return (dispatch) => {
    dispatch(changePasswordRequest());
    serviceChangePassword(payload)
      .then(response => dispatch(changePasswordSuccess(response.data)))
      .catch(err => dispatch(changePasswordFailure(err)))
  }
}

export {
  signInRequest,
  signInSuccess,
  signInFailure,
  cleanUpAuth,
  fetchSignIn,
  fetchSignOut,
  fetchParameters,
  fetchParametersNoLoader,
  fetchChangePassword,
  setFiltersDispatch,
  setRoleSelectedDispatch,
  authUser,
  cleanUpPassword,
  cleanUpMessageAuth
};

export default authSlice.reducer;
